import axios from "axios";
import { getSessionToken } from "./sessionToken";
import { Capacitor, CapacitorHttp } from "@capacitor/core";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const nativePlatform = Capacitor.isNativePlatform();
const iosNative = nativePlatform && Capacitor.getPlatform() === "ios";

// Délai maximal d'une requête (audit P14) : au-delà, l'écran propose de réessayer.
export const REQUEST_TIMEOUT_MS = 20000;

// iOS : le transport natif ne connaît ni l'annulation ni le délai d'Axios (audit P11/P14).
// On lui transmet le délai, et la promesse est rejetée immédiatement à l'annulation ou à l'expiration :
// l'écran n'attend plus une réponse devenue inutile (elle est ignorée si elle arrive ensuite).
const iosGetAdapter = async (config) => {
  const headers = Object.fromEntries(
    Object.entries(config.headers?.toJSON?.() || config.headers || {})
      .filter(([, value]) => value != null)
      .map(([key, value]) => [key, String(value)])
  );
  const timeout = config.timeout || REQUEST_TIMEOUT_MS;
  if (config.signal?.aborted) throw new axios.CanceledError(null, config);
  let cleanup = () => {};
  const guard = new Promise((_, reject) => {
    const onAbort = () => reject(new axios.CanceledError(null, config));
    const timer = setTimeout(() => reject(new axios.AxiosError(`timeout of ${timeout}ms exceeded`,
      axios.AxiosError.ECONNABORTED, config)), timeout);
    config.signal?.addEventListener?.("abort", onAbort, { once: true });
    cleanup = () => { clearTimeout(timer); config.signal?.removeEventListener?.("abort", onAbort); };
  });
  let nativeResponse;
  try {
    nativeResponse = await Promise.race([
      CapacitorHttp.get({ url: api.getUri(config), headers, responseType: "json",
        connectTimeout: timeout, readTimeout: timeout }),
      guard,
    ]);
  } finally {
    cleanup();
  }
  const response = {
    data: nativeResponse.data,
    status: nativeResponse.status,
    statusText: "",
    headers: nativeResponse.headers,
    config,
    request: null,
  };
  const valid = config.validateStatus
    ? config.validateStatus(response.status)
    : response.status >= 200 && response.status < 300;
  if (!valid) {
    throw new axios.AxiosError(
      `Request failed with status code ${response.status}`,
      axios.AxiosError.ERR_BAD_RESPONSE,
      config,
      null,
      response
    );
  }
  return response;
};

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  // Le web utilise le cookie HttpOnly. Les conteneurs Capacitor utilisent le
  // Bearer ci-dessous : demander aussi des cookies cross-site peut conduire
  // WKWebView à interrompre la requête après son preflight CORS.
  // Android conserve ici son comportement déjà validé. Seul iOS évite les
  // cookies cross-site, puisqu'il s'authentifie par Bearer.
  withCredentials: !iosNative,
  timeout: REQUEST_TIMEOUT_MS,
});

api.interceptors.request.use((cfg) => {
  // Web : authentification par cookie HttpOnly uniquement — aucun token en JS,
  // ce qui protège contre le vol de session via XSS. Natif (Capacitor) : le
  // cookie cross-site n'est pas envoyé de façon fiable par le webview, on
  // conserve donc le Bearer depuis le stockage local pour l'app iOS/Android.
  if (nativePlatform) {
    const t = getSessionToken(); // C10 : Keychain / Keystore sur l'app native
    // Restoration binds proofs to the session that initiated the operation.
    // Check here too: Axios runs interceptors after the caller's synchronous check.
    if (cfg.expectedSession !== undefined && t !== cfg.expectedSession) {
      throw new Error("Le compte a changé. Reconnecte-toi puis restaure tes achats.");
    }
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
  }
  if (iosNative && cfg.method?.toLowerCase() === "get") {
    cfg.adapter = iosGetAdapter;
  }
  cfg.headers["Accept-Language"] = localStorage.getItem("hitmax_lang") || "fr";
  return cfg;
});

export default api;
